/**
 * SearchResults — post-search results shell for the unified search
 * surface (VOY-475 structural extraction, evolved through VOY-477).
 *
 *   - Reads `useSearchState()` for the search chips so the Flights /
 *     Hotels / Itinerary tabs hide when their chip is off (VOY-471/473).
 *   - Renders the design's tab strip + List/Both/Map view toggle.
 *   - Reuses the existing inner panes verbatim — FlightTabContent,
 *     HotelTabContent, CollapsibleItineraryPreview — so all booking,
 *     filtering, sorting, cart, and Stripe wiring downstream is untouched.
 *   - Inserts a Subscribe-to-book interstitial mid-list on the Flights tab.
 *
 * Mounted by `LandingV6` in State B (VOY-477). Result-pipeline data
 * (flights, hotels, itinerary, loading flags) flows in via props from
 * the parent's `useSearchPipeline`. The legacy `search-plus.tsx` page
 * that originally consumed this shell was deleted in VOY-478 — the
 * unified `/` route is the only mount path now.
 */
import { useEffect, useMemo, useState } from 'react'
import { RefreshCw, Filter } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { CollapsibleItineraryPreview } from '@/components/travel/CollapsibleItineraryPreview'
import { FlightTabContent } from '@/components/search-plus/FlightTabContent'
import { HotelTabContent } from '@/components/search-plus/HotelTabContent'
import type { HotelFilters } from '@/components/search/FilterRow'
import type {
  FlightResult,
  Hotel,
  TripType,
  MultiCitySegment,
  ItineraryResponse,
  FlightSearchParamsForAnalysis,
} from '@/types/travel'
import type { RetryState } from '@/lib/hotels-api-backend'
import { useSearchState } from '@/lib/search/useSearchState'
import { SearchResultsTabs, type ResultTabKey, type ResultViewMode } from './SearchResultsTabs'

const retryBannerBaseClass =
  'flex items-center justify-center gap-2 text-amber-600 text-sm py-2 px-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg'

function RetryBanner({ retryState, className }: { retryState: RetryState; className?: string }) {
  return (
    <div role="status" aria-live="polite" className={cn(retryBannerBaseClass, className)}>
      <RefreshCw className="h-4 w-4 animate-spin" />
      <span>
        {retryState.isConnectionError
          ? `Connection issue detected. Retrying (${retryState.attempt}/${retryState.maxRetries})...`
          : `Search taking longer than expected. Retrying (${retryState.attempt}/${retryState.maxRetries})...`}
      </span>
    </div>
  )
}

interface RegionSuggestion {
  id: string | number
  name: string
  country_code?: string
  lat?: number
  lon?: number
  type?: string
}

export interface SearchResultsProps {
  flights: FlightResult[]
  hotels: Hotel[]
  isLoading: boolean
  isLoadingFlights?: boolean
  isLoadingHotels?: boolean
  isLoadingItinerary?: boolean
  itinerary: ItineraryResponse | null
  isGeneratingItinerary: boolean
  tripType?: TripType
  segments?: MultiCitySegment[]
  hotelCheckin?: string
  hotelCheckout?: string
  hotelGuests?: Array<{ adults: number; children: number[] }>
  hotelTotal?: number
  hotelPage?: number
  hotelPerPage?: number
  hotelTotalPages?: number
  onHotelPageChange?: (page: number) => void
  searchedForFlights?: boolean
  searchedForHotels?: boolean
  searchedForItinerary?: boolean
  suggestions?: RegionSuggestion[]
  widened?: Hotel[]
  onSelectSuggestion?: (s: RegionSuggestion) => void
  /**
   * VOY-489 fix: hotel filters are now controlled from LandingV6
   * (lifted up to survive HotelTabContent's unmount/remount between
   * loading branches in this component).
   */
  hotelFilters?: HotelFilters
  onHotelFiltersChange?: (filters: HotelFilters) => void
  hotelViewMode?: ResultViewMode
  onHotelViewModeChange?: (mode: ResultViewMode) => void
  totalHotelsBeforeFilters?: number
  hasActiveFilters?: boolean
  onClearFilters?: () => void
  searchParams?: FlightSearchParamsForAnalysis
  hotelRetryState?: RetryState | null
  // VOY-466: Match-My-Taste toggle state, lifted up to LandingV6 which
  // owns the search pipeline. Optional — when omitted, HotelTabContent
  // hides the toggle (back-compat for non-LandingV6 callers).
  personalizeHotels?: boolean
  onPersonalizeHotelsChange?: (next: boolean) => void
  // VOY-461: flight "Match my taste" parity.
  personalizeFlights?: boolean
  onPersonalizeFlightsChange?: (next: boolean) => void
  // Note: an `onClose` slot for the State A↔B transition was declared in
  // the ticket contract, but no caller consumes it yet. Will be re-added
  // when VOY-477's route state machine wires the clear-search affordance.
}

const loadingMessages = [
  'Searching the best flights for you...',
  'Finding perfect hotels...',
  'Comparing thousands of options...',
  'Analyzing flight schedules...',
  'Scanning hotel reviews...',
  'Finding the best prices...',
  'Getting exclusive deals...',
  'Optimizing your travel plan...',
  'Matching your preferences...',
  'Checking availability...',
  'Securing your options...',
  'Almost ready...',
]

export function SearchResults({
  flights,
  hotels,
  isLoading,
  isLoadingFlights = false,
  isLoadingHotels = false,
  isLoadingItinerary = false,
  itinerary,
  isGeneratingItinerary,
  tripType: _tripType,
  segments: _segments,
  hotelCheckin,
  hotelCheckout,
  hotelGuests,
  hotelTotal,
  hotelPage,
  hotelPerPage,
  hotelTotalPages,
  onHotelPageChange,
  searchedForFlights = true,
  searchedForHotels = true,
  searchedForItinerary = true,
  suggestions = [],
  widened = [],
  onSelectSuggestion,
  hotelFilters,
  onHotelFiltersChange,
  hotelViewMode = 'list',
  onHotelViewModeChange,
  totalHotelsBeforeFilters = 0,
  hasActiveFilters = false,
  onClearFilters,
  searchParams,
  hotelRetryState,
  personalizeHotels,
  onPersonalizeHotelsChange,
  personalizeFlights,
  onPersonalizeFlightsChange,
}: SearchResultsProps) {
  // Per-tab chip visibility comes from the shared search state. When the
  // user toggles "Hotels" off in the composer chips, the Hotels tab here
  // disappears. We still respect the `searchedFor*` data-availability
  // flags coming from the parent for cases where chips are on but a
  // particular search wasn't executed yet.
  const search = useSearchState()
  const chips = search.state.chips

  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)
  useEffect(() => {
    if (!isLoading) return
    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [isLoading])

  // What kinds of results do we have content for?
  const hasFlightResults = chips.flights && searchedForFlights && flights.length > 0
  const hasHotelResults =
    chips.hotels && searchedForHotels && (hotels.length > 0 || totalHotelsBeforeFilters > 0)
  const hasItineraryResults =
    chips.itinerary && searchedForItinerary && !!itinerary

  // Default active tab: first available content tab.
  const initialTab: ResultTabKey = hasFlightResults
    ? 'flights'
    : hasHotelResults
      ? 'hotels'
      : 'itinerary'
  const [activeTab, setActiveTab] = useState<ResultTabKey>(initialTab)

  // If the active tab disappears (chip turned off, or no data), fall back
  // to the next available one. Without this the user gets a blank pane.
  useEffect(() => {
    const stillVisible =
      (activeTab === 'flights' && chips.flights && searchedForFlights) ||
      (activeTab === 'hotels' && chips.hotels && searchedForHotels) ||
      (activeTab === 'itinerary' && chips.itinerary && searchedForItinerary)
    if (stillVisible) return
    if (chips.flights && searchedForFlights) setActiveTab('flights')
    else if (chips.hotels && searchedForHotels) setActiveTab('hotels')
    else if (chips.itinerary && searchedForItinerary) setActiveTab('itinerary')
  }, [activeTab, chips, searchedForFlights, searchedForHotels, searchedForItinerary])

  // Built before any conditional early-return to keep hook order stable.
  // `itinerary.data.timeline.length` is the source for the "Nd" badge; the
  // `data` field is a union of StructuredItinerary | string (HTML).
  const visibleTabs = useMemo(() => {
    const tabs: Array<{
      key: ResultTabKey
      label: string
      count?: string | number
      loading?: boolean
    }> = []
    if (chips.flights && searchedForFlights) {
      tabs.push({
        key: 'flights',
        label: 'Flights',
        count: flights.length > 0 ? flights.length : undefined,
        loading: isLoadingFlights,
      })
    }
    if (chips.hotels && searchedForHotels) {
      const total = hotelTotal ?? hotels.length
      tabs.push({
        key: 'hotels',
        label: 'Hotels',
        count: total > 0 ? total : undefined,
        loading: isLoadingHotels,
      })
    }
    if (chips.itinerary && searchedForItinerary) {
      const days =
        itinerary && typeof itinerary.data === 'object'
          ? itinerary.data.timeline?.length
          : undefined
      tabs.push({
        key: 'itinerary',
        label: 'Itinerary',
        count: days ? `${days}d` : undefined,
        loading: isLoadingItinerary || isGeneratingItinerary,
      })
    }
    return tabs
  }, [
    chips.flights,
    chips.hotels,
    chips.itinerary,
    searchedForFlights,
    searchedForHotels,
    searchedForItinerary,
    flights.length,
    hotels.length,
    hotelTotal,
    itinerary,
    isLoadingFlights,
    isLoadingHotels,
    isLoadingItinerary,
    isGeneratingItinerary,
  ])

  // ---------------- Loading-state shortcuts ----------------
  // Preserved verbatim from the old SearchResults: a hotel-only search
  // shows the hotel skeleton immediately, before any other layout, so
  // users see structure within milliseconds even on a slow flights call.
  const isHotelOnlySearch = searchedForHotels && !searchedForFlights
  const isHotelsLoadingWithFlightsDone =
    isLoadingHotels && searchedForHotels && searchedForFlights && !isLoadingFlights

  if (
    isLoadingHotels &&
    searchedForHotels &&
    chips.hotels &&
    (isHotelOnlySearch || isHotelsLoadingWithFlightsDone)
  ) {
    return (
      <div className="space-y-4">
        <SearchResultsTabs
          active="hotels"
          onChange={setActiveTab}
          tabs={[{ key: 'hotels', label: 'Hotels', loading: true }]}
        />
        <div id="sr-pane-hotels" role="tabpanel" aria-labelledby="sr-tab-hotels">
          {hotelRetryState?.isRetrying && <RetryBanner retryState={hotelRetryState} />}
          <HotelTabContent
            hotels={[]}
            hotelCheckin={hotelCheckin}
            hotelCheckout={hotelCheckout}
            hotelGuests={hotelGuests}
            filters={hotelFilters}
            onFiltersChange={onHotelFiltersChange}
            isLoading={true}
            personalizeEnabled={personalizeHotels}
            onPersonalizeChange={onPersonalizeHotelsChange}
          />
        </div>
      </div>
    )
  }

  if (
    (isLoading || isLoadingFlights || isLoadingItinerary) &&
    !(isLoadingHotels && searchedForHotels && !searchedForFlights)
  ) {
    return (
      <div className="max-w-md mx-auto">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            <span className="text-gray-700 font-medium animate-pulse">
              {loadingMessages[loadingMessageIndex]}
            </span>
            {hotelRetryState?.isRetrying && (
              <RetryBanner retryState={hotelRetryState} className="mt-2" />
            )}
          </div>
        </div>
      </div>
    )
  }

  // Nothing requested at all (e.g., user landed on the route without a
  // search yet). Parent handles the empty-state hero in that case.
  if (!searchedForFlights && !searchedForHotels && !searchedForItinerary) {
    return null
  }

  // Searched but nothing came back (and not still spinning up an itinerary).
  if (!hasFlightResults && !hasHotelResults && !hasItineraryResults && !isGeneratingItinerary) {
    const isFilteredOut =
      searchedForHotels &&
      totalHotelsBeforeFilters > 0 &&
      hotels.length === 0 &&
      hasActiveFilters
    return (
      <div className="max-w-md mx-auto">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
          <div className="text-center space-y-4">
            <div className="text-gray-600">
              {isFilteredOut
                ? `No hotels match your filters. You have ${totalHotelsBeforeFilters} hotel${
                    totalHotelsBeforeFilters !== 1 ? 's' : ''
                  } available.`
                : 'No results found for your search criteria.'}
            </div>
            {isFilteredOut && onClearFilters && (
              <Button variant="outline" size="sm" onClick={onClearFilters} className="rounded-full">
                <Filter className="h-4 w-4 mr-2" />
                Clear all filters
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // The view toggle is meaningful for hotels (list/both/map) only — flights
  // and itinerary panes have no map dimension. Hide it elsewhere.
  const showViewToggle = activeTab === 'hotels' && !!onHotelViewModeChange

  const hasTabbedContent = visibleTabs.length > 0

  return (
    <div className="space-y-6">
      {/* VOY-475: itinerary is now a dedicated result tab (per design),
          not a spotlight above the tabs. Previously the page rendered
          CollapsibleItineraryPreview twice (above + inside its tab) and
          double-mounted it. The Itinerary tab below is the single source. */}
      {hasTabbedContent && (
        <div className="sr-shell">
          <SearchResultsTabs
            active={activeTab}
            onChange={setActiveTab}
            tabs={visibleTabs}
            view={
              showViewToggle
                ? {
                    value: hotelViewMode,
                    onChange: (next) => onHotelViewModeChange?.(next),
                  }
                : undefined
            }
          />

          {activeTab === 'flights' && chips.flights && searchedForFlights && (
            <div
              id="sr-pane-flights"
              role="tabpanel"
              aria-labelledby="sr-tab-flights"
              className="sr-pane"
            >
              {/* Single mount: FlightTabContent owns the interstitial via
                  `interstitialAfterIndex` so its FilterBar / filter state /
                  header count stay unified. Splitting into two mounts (as
                  an earlier draft did) duplicated the filter UI and CTA
                  for non-subscribers — see PR #278 review. */}
              <FlightTabContent
                flights={flights}
                itinerary={itinerary || undefined}
                isLoading={isLoadingFlights || isLoading}
                interstitialAfterIndex={2}
                personalizeEnabled={personalizeFlights}
                onPersonalizeChange={onPersonalizeFlightsChange}
              />
            </div>
          )}

          {activeTab === 'hotels' && chips.hotels && searchedForHotels && (
            <div
              id="sr-pane-hotels"
              role="tabpanel"
              aria-labelledby="sr-tab-hotels"
              className="sr-pane"
            >
              {hotelRetryState?.isRetrying && <RetryBanner retryState={hotelRetryState} />}
              <HotelTabContent
                hotels={hotels}
                hotelCheckin={hotelCheckin}
                hotelCheckout={hotelCheckout}
                hotelGuests={hotelGuests}
                hotelTotal={hotelTotal}
                hotelPage={hotelPage}
                hotelPerPage={hotelPerPage}
                hotelTotalPages={hotelTotalPages}
                onHotelPageChange={onHotelPageChange}
                suggestions={suggestions}
                widened={widened}
                onSelectSuggestion={onSelectSuggestion}
                filters={hotelFilters}
                onFiltersChange={onHotelFiltersChange}
                isLoading={isLoadingHotels || isLoading}
                viewMode={hotelViewMode}
                onViewModeChange={onHotelViewModeChange}
                personalizeEnabled={personalizeHotels}
                onPersonalizeChange={onPersonalizeHotelsChange}
              />
            </div>
          )}

          {activeTab === 'itinerary' && chips.itinerary && searchedForItinerary && (
            <div
              id="sr-pane-itinerary"
              role="tabpanel"
              aria-labelledby="sr-tab-itinerary"
              className="sr-pane"
            >
              {/* The itinerary preview already renders above the tabs;
                  the dedicated tab is intentionally a thin wrapper that
                  keeps focus on the same content for keyboard users
                  navigating via the tab strip. */}
              <CollapsibleItineraryPreview
                isLoading={isLoadingItinerary || isGeneratingItinerary}
                itinerary={itinerary}
                availableFlights={flights}
                availableHotels={hotels}
                searchParams={searchParams}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
